/**
 * A file's rows, read a page at a time: the summary, a page, the values a
 * filter can offer, and the export ticket.
 */
import { z } from 'zod';

export const fileRowColumnTypeSchema = z.enum([
	'text',
	'date',
	'money',
	'decimal',
	'integer',
]);

export const fileRowColumnSchema = z.object({
	key: z.string(),
	type: fileRowColumnTypeSchema,
});

export const fileRowsSummarySchema = z.object({
	/* 'audit' — the rows locked into the audit; 'file' — the file's own rows. */
	source: z.enum(['file', 'audit']),
	rowCount: z.number(),
	byClassification: z.record(z.string(), z.number()).nullable(),
	columns: z.array(fileRowColumnSchema),
	searchable: z.array(z.string()),
	filterable: z.array(z.string()),
	dateRange: z
		.object({ min: z.string().nullable(), max: z.string().nullable() })
		.nullable(),
});

export const fileRowCellSchema = z.union([z.string(), z.number(), z.null()]);

export const fileRowsPageSchema = z.object({
	columns: z.array(fileRowColumnSchema),
	rows: z.array(z.array(fileRowCellSchema)),
	total: z.number(),
	page: z.number(),
	pageSize: z.number(),
});

export const fileRowValuesSchema = z.object({
	values: z.array(z.object({ value: z.string(), count: z.number() })),
	truncated: z.boolean(),
});

export const fileRowsExportSchema = z.object({
	url: z.string(),
	filename: z.string(),
	rowCount: z.number(),
});

export type FileRowColumn = z.infer<typeof fileRowColumnSchema>;
export type FileRowCell = z.infer<typeof fileRowCellSchema>;
export type FileRowsSummary = z.infer<typeof fileRowsSummarySchema>;
export type FileRowsPage = z.infer<typeof fileRowsPageSchema>;
export type FileRowValues = z.infer<typeof fileRowValuesSchema>;
export type FileRowsExport = z.infer<typeof fileRowsExportSchema>;

/** The filters every rows call shares. */
export interface FileRowFilters {
	q?: string;
	classification?: string;
	plantCode?: string;
	documentType?: string;
	periodStart?: string;
	periodEnd?: string;
}

export interface FileRowsQuery extends FileRowFilters {
	page: number;
	pageSize: number;
	sort?: string;
	dir?: 'asc' | 'desc';
}
